import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { TooltipProvider } from '../ui/tooltip';
import { Undo2, Redo2, Save, Sun, Moon, ChevronUp, ChevronDown, Pin } from 'lucide-react';
import { WannaPublishMark } from '../editor/WannaPublishLogo';
import { AccountMenu } from '../editor/AccountMenu';
import { HelpButton } from '../editor/HelpButton';
import { CONTEXT_TABS, contextTabsForSelection } from '../../lib/selection';
import { WithTip } from './RibbonPrimitives';
import { HomeTab } from './HomeTab';
import { InsertTab } from './InsertTab';
import { PageDesignTab } from './PageDesignTab';
import { ViewTab } from './ViewTab';
import { TextBoxTab } from './TextBoxTab';
import { ShapeFormatTab } from './ShapeFormatTab';
import { PictureFormatTab } from './PictureFormatTab';
import { TableDesignTab } from './TableDesignTab';
import { MasterPageTab } from './MasterPageTab';
import { DocumentTabs } from './DocumentTabs';
import { Backstage } from './Backstage';
import { useFormatPainter } from '../../hooks/useFormatPainter';
import { toast } from 'sonner';

const MAIN_TABS = [
  { id: 'home', label: 'Home' },
  { id: 'insert', label: 'Insert' },
  { id: 'page-design', label: 'Page Design' },
  { id: 'view', label: 'View' },
];

const CONTEXT_META = {
  'text-box': { label: 'Text Box', color: 'hsl(var(--wp-context-teal))', kind: 'text' },
  'shape-format': { label: 'Shape Format', color: 'hsl(var(--wp-context-amber))', kind: 'shape' },
  'picture-format': { label: 'Picture Format', color: 'hsl(var(--wp-context-green))', kind: 'image' },
  'table-design': { label: 'Table Design', color: 'hsl(var(--primary))', kind: 'table' },
  'master-page': { label: 'Master Page', color: 'hsl(var(--wp-context-amber))', kind: 'master' },
};

const PAINTABLE_KEYS = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textDecoration', 'color', 'textAlign', 'lineHeight', 'letterSpacing', 'tracking', 'textScale', 'fill', 'stroke', 'background', 'cornerRadius', 'opacity', 'shadow', 'textFill', 'textOutline', 'textEffects', 'borderRadius'];

export const RibbonBar = () => {
  const { state, dispatch, rawDispatch, undo, redo, canUndo, canRedo, getSelectedObjects, openDialog, hasUnsavedChanges } = useEditor();
  const selected = getSelectedObjects();
  // Master Page view adds Publisher's contextual "Master Page" tab in front of the selection tabs
  const contextKey = [...(state.viewMode === 'master' ? ['master-page'] : []), ...contextTabsForSelection(selected, state.editingObjectId)].join(',');
  const contextTabs = useMemo(() => (contextKey ? contextKey.split(',') : []), [contextKey]);
  const [collapsed, setCollapsed] = useState(false);
  const [peek, setPeek] = useState(false);
  const [name, setName] = useState(state.projectName);
  useEffect(() => setName(state.projectName), [state.projectName]);

  // Revert to Home when the contextual tab no longer applies
  useEffect(() => {
    if ((CONTEXT_TABS.has(state.ribbonTab) || state.ribbonTab === 'master-page') && !contextTabs.includes(state.ribbonTab)) {
      rawDispatch({ type: 'SET_RIBBON_TAB', payload: 'home' });
    }
  }, [state.ribbonTab, contextTabs, rawDispatch]);

  // Format painter — text mode (highlight → paint) lives in useFormatPainter; object mode applies the stored object
  // formatting to the next selection (one use, or every selection after a double-click until Esc). The effect runs when
  // the painter is armed (that run only remembers it — the source is still the selection) and on every later selection
  // change (those paint). Depending on the selection alone swallowed the first selection change after arming.
  useFormatPainter();
  const painterArmedRef = useRef(null);
  useEffect(() => {
    const fp = state.formatPainter;
    if (!fp || fp.mode !== 'object') { painterArmedRef.current = null; return; }
    if (painterArmedRef.current !== fp) { painterArmedRef.current = fp; return; } // just armed (or made sticky)
    const targets = selected.filter((o) => o.id !== fp.sourceId);
    if (!targets.length) return;
    const props = {};
    PAINTABLE_KEYS.forEach((k) => { if (fp.properties[k] !== undefined) props[k] = fp.properties[k]; });
    dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: targets.map((o) => o.id), props } });
    if (!fp.sticky) rawDispatch({ type: 'SET_FORMAT_PAINTER', payload: null });
    toast.success(fp.sticky ? 'Formatting applied — select another object, or press Esc to stop.' : 'Formatting applied.', { id: 'format-painter' });
    // deliberately keyed on the selection ids + painter only: `selected` also changes with every property edit — the paint
    // above included — so listing it would re-paint after its own dispatch; dispatch / rawDispatch are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedObjectIds, state.formatPainter]);

  const setTab = (id) => {
    rawDispatch({ type: 'SET_RIBBON_TAB', payload: id });
    if (collapsed) setPeek(true);
  };

  const tabs = useMemo(() => {
    const list = [...MAIN_TABS];
    contextTabs.forEach((id) => list.push({ id, contextual: true, ...CONTEXT_META[id] }));
    return list;
  }, [contextTabs]);

  const active = state.ribbonTab;
  const showBody = !collapsed || peek;

  const body = (() => {
    switch (active) {
      case 'insert': return <InsertTab />;
      case 'page-design': return <PageDesignTab />;
      case 'view': return <ViewTab />;
      case 'text-box': return <TextBoxTab />;
      case 'shape-format': return <ShapeFormatTab />;
      case 'picture-format': return <PictureFormatTab />;
      case 'table-design': return <TableDesignTab />;
      case 'master-page': return <MasterPageTab />;
      default: return <HomeTab />;
    }
  })();

  const qa = 'h-7 w-7 rounded-sm flex items-center justify-center text-foreground hover:bg-[hsl(var(--wp-command-hover))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150';

  return (
    <TooltipProvider delayDuration={500}>
      <div className="relative z-50 shrink-0 select-none" data-ribbon data-testid="ribbon-bar" onMouseLeave={() => peek && setPeek(false)}>
        {/* Row 0: open publications (document tabs) */}
        <DocumentTabs />
        {/* Row 1: File + tabs + quick access */}
        <div className="wp-tabstrip h-8 flex items-end px-1.5 gap-0.5 overflow-x-auto overflow-y-hidden wp-ribbon-scroll [scrollbar-width:none]" data-testid="ribbon-tab-strip">
          <div className="flex items-center gap-1.5 self-center mr-1 pl-1">
            <WannaPublishMark size={22} />
          </div>
          <button
            type="button"
            onClick={() => rawDispatch({ type: 'SET_FILE_OPEN', payload: true })}
            className="h-7 px-3.5 shrink-0 rounded-t-sm bg-primary text-primary-foreground text-[12px] font-semibold hover:bg-primary/90 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="ribbon-file-tab"
          >
            File
          </button>
          {tabs.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`relative h-7 px-3 shrink-0 whitespace-nowrap rounded-t-sm text-[12px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? 'bg-[hsl(var(--wp-ribbon))] text-foreground border border-b-0 border-[hsl(var(--wp-ribbon-border))] -mb-px' : 'text-muted-foreground hover:bg-[hsl(var(--wp-command-hover))] hover:text-foreground'}`}
                style={t.contextual ? { color: isActive ? undefined : t.color, borderTop: isActive ? `2px solid ${t.color}` : undefined, background: isActive ? undefined : `${t.color.replace(')', ' / 0.10)')}` } : undefined}
                data-testid={t.contextual ? `ribbon-context-tab-${t.id}` : `ribbon-tab-${t.id}`}
                data-state={isActive ? 'active' : 'inactive'}
                data-context={t.contextual ? t.kind : undefined}
              >
                {t.label}
              </button>
            );
          })}

          <div className="flex-1" />

          {/* Quick access */}
          <div className="flex items-center gap-0.5 self-center pr-1 shrink-0" data-testid="ribbon-quick-access">
            <div className="hidden md:flex items-center relative">
              <input
                value={name}
                onChange={(e) => { setName(e.target.value); dispatch({ type: 'SET_PROJECT_NAME', payload: e.target.value }); }}
                className="h-6 w-44 rounded-sm bg-transparent border border-transparent hover:border-border focus:border-ring px-2 text-[12px] text-foreground text-center focus:outline-none"
                title={hasUnsavedChanges ? 'Publication name — unsaved changes' : 'Publication name'}
                data-testid="project-name-input"
              />
              {hasUnsavedChanges && <span className="absolute -right-1 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-primary" title="Unsaved changes" aria-label="Unsaved changes" data-testid="unsaved-indicator" />}
            </div>
            <WithTip tip="Undo (Ctrl+Z)"><button type="button" className={qa} onClick={undo} disabled={!canUndo} data-testid="undo-btn"><Undo2 className="h-4 w-4" /></button></WithTip>
            <WithTip tip="Redo (Ctrl+Y)"><button type="button" className={qa} onClick={redo} disabled={!canRedo} data-testid="redo-btn"><Redo2 className="h-4 w-4" /></button></WithTip>
            <WithTip tip="Save (Ctrl+S)"><button type="button" className={`${qa} hidden sm:flex`} onClick={() => openDialog('save-project')} data-testid="save-btn"><Save className="h-4 w-4" /></button></WithTip>
            <WithTip tip={state.theme === 'dark' ? 'Light mode' : 'Dark mode'}><button type="button" className={`${qa} hidden sm:flex`} onClick={() => dispatch({ type: 'SET_THEME', payload: state.theme === 'dark' ? 'light' : 'dark' })} data-testid="toggle-theme-btn">{state.theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button></WithTip>
            <WithTip tip={collapsed ? 'Pin the ribbon' : 'Collapse the ribbon (Ctrl+F1)'}><button type="button" className={qa} onClick={() => { setCollapsed((c) => !c); setPeek(false); }} data-testid="ribbon-collapse-btn">{collapsed ? <Pin className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}</button></WithTip>
            <HelpButton className={qa} />
            <AccountMenu />
          </div>
        </div>

        {/* Row 2: ribbon surface */}
        {showBody && (
          <div className={`wp-ribbon-surface ${collapsed ? 'absolute left-0 right-0 shadow-lg z-50' : ''}`} data-testid={`ribbon-panel-${active}`}>
            <div className="h-[94px] flex items-stretch overflow-x-auto overflow-y-hidden wp-ribbon-scroll px-1">
              {body}
            </div>
          </div>
        )}
        {collapsed && !peek && <div className="h-0.5 wp-ribbon-surface" />}
        {collapsed && peek && (
          <button type="button" className="absolute right-2 top-9 z-[60] h-5 w-5 rounded-sm bg-popover border border-border flex items-center justify-center" onClick={() => setPeek(false)} title="Hide"><ChevronDown className="h-3 w-3" /></button>
        )}
      </div>
      {state.fileOpen && <Backstage />}
    </TooltipProvider>
  );
};
